
import React from "react"
import { FormTextInput, TextInput } from "../textInput";
import { FormTextAreaInput, TextArea } from "../textArea";
import { CheckBox, ICheckBoxProps } from "../checkBox2";
import { Switch, ISwitchProps } from "../switch";
import { DatePickerForms, DatePicker } from "../datepicker";
import { DateTimePickerProps, DateTimePickerForms } from "../DateTimePicker";
import { PropertiesNamesOfType } from "../../componentExtensions";
import { FormItem, OpenFormsContext, IContextProps } from "./OpenForms";
import { Prompt } from "react-router-dom";

interface FormTextProps<T> extends Omit<TextInput<T>["props"], "name" | "value" | "onChange" | "error"> {
    name: PropertiesNamesOfType<T, string>
}
class FormText<T> extends React.PureComponent<FormTextProps<T>>{
    render() {
        const { name } = this.props;
        return <FormItem<T>>
            {
                ({ errors, values, set }) => {
                    const value = (values[name] ?? "") as string;
                    return <FormTextInput<T>
                        {...this.props}
                        onChange={e => set(name, e.target.value as any)}
                        value={value}
                        error={errors[name]}
                        name={name as any}
                        autoComplete="chrome-off"
                    />
                }
            }
        </FormItem>;
    }
}

interface FormTextAreaProps<T> extends Omit<TextArea<T>["props"], "name" | "value" | "onChange" | "error"> {
    name: PropertiesNamesOfType<T, string>
}
class FormTextArea<T> extends React.PureComponent<FormTextAreaProps<T>>{
    render() {
        const { name } = this.props;
        return <FormItem<T>>
            {
                ({ errors, values, set }) => {
                    const value = (values[name] ?? "") as string;
                    return <FormTextAreaInput<T>
                        {...this.props}
                        onChange={e => set(name, e.target.value as any)}
                        value={value}
                        error={errors[name]}
                        name={name as any}
                    />
                }
            }
        </FormItem>;
    }
}
interface FormNumberProps<T> extends Omit<TextInput<T>["props"], "name" | "value" | "onChange" | "error"> {
    name: PropertiesNamesOfType<T, number>
}
class FormFloat<T> extends React.PureComponent<FormNumberProps<T>>{
    render() {
        const { name, ...props } = this.props;
        return <FormItem<T>>
            {
                ({ set, errors, values, }) => {
                    const value = +(values[name] ?? "");
                    const [state, setState] = React.useState(value)
                    return <FormTextInput<T>
                        {...props}
                        type="text"
                        onChange={e => {
                            const value = +e.target.value.replace(",", ".");
                            setState(value);
                            if (!isNaN(value))
                                set(name, value as any)
                        }}
                        defaultValue={value}
                        error={!isNaN(state) ? errors[name] : "deve essere un numero"}
                        name={name as any}
                    />
                }
            }
        </FormItem>;
    }
}
class FormInteger<T> extends React.PureComponent<FormNumberProps<T>>{
    render() {
        const { name, ...props } = this.props;
        return <FormItem<T>>
            {
                ({ set, errors, values, }) => {
                    const value = +(values[name] ?? "");
                    const [state, setState] = React.useState(value)
                    return <FormTextInput<T>
                        {...props}
                        type="text"
                        onChange={e => {
                            const value = +e.target.value.replace(",", ".");
                            setState(value);
                            if (!isNaN(value) && Math.floor(value) === value)
                                set(name, value as any)
                        }}
                        defaultValue={value}
                        error={!isNaN(state) && Math.floor(state) === state ? errors[name] : "deve essere un numero intero"}
                        name={name as any}
                    />
                }
            }
        </FormItem>;
    }
}

type FormCheckProps<T> = Omit<ICheckBoxProps, "checked" | "onChange" | "name" | "error"> & { name: PropertiesNamesOfType<T, boolean> };
const FormCheck = <T,>(props: FormCheckProps<T>) => {
    const { name, ...p } = props;
    return <div className="mb-3">
        <FormItem<T>>
            {context => <CheckBox
                {...p}
                name={name?.toString()}
                checked={context.values[name] as any}
                onChange={value => context.set(name, value as any)}
                error={context.errors[props.name]}
            />}
        </FormItem>
    </div>
}


type FormSwitchProps<T> = Omit<ISwitchProps, "checked" | "onChange" | "name" | "error"> & { name: PropertiesNamesOfType<T, boolean> };
const FormSwitch = <T,>(props: FormSwitchProps<T>) => {
    const { name, ...p } = props;
    return <div className="mb-3">
        <FormItem<T>>
            {context => <Switch
                {...p}
                name={name?.toString()}
                checked={context.values[name] as any}
                onChange={value => context.set(name, value as any)}
                error={context.errors[props.name]}
            />}
        </FormItem>
    </div>
}

type FormDateTimeProps<T> = Omit<DateTimePickerProps, "value" | "onSelectDate"> & { name: PropertiesNamesOfType<T, Date | undefined> };
const FormDateTime = <T,>(props: FormDateTimeProps<T>) => {
    return <FormItem<T>>
        {context => <DateTimePickerForms
            {...props}
            value={context.values[props.name] as any}
            onSelectDate={date => context.set(props.name, date as any)}
            error={context.errors[props.name]}
        />}
    </FormItem>
}
type FormDateProps<T> = Omit<DatePicker["props"], "value" | "onSelectDate"> & { name: PropertiesNamesOfType<T, Date | undefined> };
const FormDate = <T,>(props: FormDateProps<T>) => {
    return <FormItem<T>>
        {context => <DatePickerForms
            {...props}
            value={context.values[props.name] as any}
            onSelectDate={date => context.set(props.name, date as any)}
            error={context.errors[props.name]}
        />}
    </FormItem>
}

export const ExitTxt = "Non hai salvato il lavoro. Vuoi uscire?";

const FormWrapper = <T,>(props: { noPrompt: boolean, initialValues: T, form: Omit<React.FormHTMLAttributes<HTMLFormElement>, "onSubmit">, context: IContextProps<T> }) => {
    const { noPrompt, context, form: { children, ...formProps } } = props;
    React.useEffect(() => {
        if (!noPrompt) {
            if (props.context.dirty) {
                window.onbeforeunload = () => ExitTxt;
                return () => { window.onbeforeunload = null };
            } else {
                window.onbeforeunload = null;
            }
        }
        return () => { };
    })
    return <>
        <Prompt when={context.dirty && !noPrompt} message={ExitTxt} />
        <form onSubmit={context.handleSubmit} {...formProps}>
            {typeof children == "function" ? children(context) : children}
        </form>
    </>;
}
export type FormProps<T> = OpenFormsContext<T>["props"] & Omit<React.FormHTMLAttributes<HTMLFormElement>, "onSubmit">;
export const Form = <T,>(props: FormProps<T>) => {

    const { onSubmit, initialValues, validationSchema, noPrompt, isEqual, ...p } = props;
    return <OpenFormsContext
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        noPrompt={noPrompt}
        isEqual={isEqual}
    >
        {
            forms => <FormWrapper context={forms} form={p} initialValues={initialValues} noPrompt={noPrompt ?? false} />
        }
    </OpenFormsContext>
}

export function OpenForms<T>() {
    const result: {
        FormText: React.ComponentType<FormTextProps<T>>,
        FormFloat: React.ComponentType<FormNumberProps<T>>,
        FormInteger: React.ComponentType<FormNumberProps<T>>,
        FormItem: (props: FormItem<T>["props"]) => React.ReactElement | null,
        Form: React.ComponentType<FormProps<T>>,
        FormCheck: React.ComponentType<FormCheckProps<T>>,
        FormSwitch: React.ComponentType<FormSwitchProps<T>>,
        FormDate: React.ComponentType<FormDateProps<T>>,
        FormDateTime: React.ComponentType<FormDateTimeProps<T>>,
        FormTextArea: React.ComponentType<FormTextAreaProps<T>>,
    } = {
        Form,
        FormItem: FormItem as any,
        FormText: FormText as any,
        FormTextArea: FormTextArea as any,
        FormFloat: FormFloat as any,
        FormInteger: FormInteger as any,
        FormCheck,
        FormSwitch,
        FormDate,
        FormDateTime,
    };
    return result;
}